import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
  Input,
  InputGroupText,
  InputGroup,
} from "reactstrap";
import { Link } from "react-router-dom";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiClient from "../BaseUrl/ApiClient";
import axios from "axios";
import PlanSelectionModal from "../Components/PlanSelectionModal";
// Remove getSubscription import - we'll use direct API call like BusinessProfile

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

function LunchDealList() {
  const token = localStorage.getItem("accessToken");
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState({
    type: '',
    method: '',
    hasPaymentCard: false
  });
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ skipPlanSelection: false, selectedPlan: null, action: 'upgrade' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeInput, setTimeInput] = useState("");
  const [editingTime, setEditingTime] = useState(false);
  const [savedTime, setSavedTime] = useState("");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: 10,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);

  // Determine planName from subscription details
  const planName = subscriptionDetails.type || "free";

  // Fetch subscription data using same API as BusinessProfile
  const fetchSubscription = async (token) => {
    try {
      const response = await axiosInstance.get("/business/payment/subscription", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const subscriptionData = response.data?.responseData?.subscription;
      
      // Check if subscription is null (free plan)
      if (!subscriptionData) {
        setSubscriptionDetails({
          type: 'free',
          method: 'Not available',
          hasPaymentCard: false
        });
        return;
      }

      // Get subscription plan from subscription type
      const subscriptionPlan = response.data?.responseData?.subscriptionType || 'standard';
      
      // Check if payment card exists (same logic as BusinessProfile)
      const hasPaymentCard = subscriptionData?.paymentCard !== null && subscriptionData?.paymentCard !== undefined;
      const paymentMethod = hasPaymentCard ? subscriptionData.subscriptionMethod : 'Not available';
      
      setSubscriptionDetails({
        type: subscriptionPlan,
        method: paymentMethod,
        hasPaymentCard: hasPaymentCard
      });

    } catch (err) {
      // If API returns 404 or subscription is null, treat as free plan
      if (err.response?.status === 404 || err.response?.data?.responseCode === 404) {
        setSubscriptionDetails({
          type: 'free',
          method: 'Not available',
          hasPaymentCard: false
        });
      } else {
        console.error("Error fetching subscription:", err);
        // Fallback to free plan on other errors
        setSubscriptionDetails({
          type: 'free',
          method: 'Not available',
          hasPaymentCard: false
        });
      }
    } finally {
      setSubscriptionLoading(false);
      setIsRefreshing(false);
    }
  };
  
  // Handle banner button click with smart modal logic
  const handleBannerButtonClick = () => {
    if (isRefreshing) return;
    
    // Check subscription and payment card status
    const hasSubscription = subscriptionDetails.type !== '' && subscriptionDetails.type !== 'free';
    const hasPaymentCard = subscriptionDetails.hasPaymentCard;
    
    if (hasSubscription) {
      // User has subscription - handle Change Plan flow
      if (hasPaymentCard) {
        // Scenario: Card exists - just select package and skip card details
        setModalConfig({ 
          skipPlanSelection: false, 
          selectedPlan: null, 
          action: 'changePlan', 
          skipCardDetails: true 
        });
      } else {
        // Scenario: No card - select package and go to card details
        setModalConfig({ 
          skipPlanSelection: false, 
          selectedPlan: null, 
          action: 'changePlan', 
          skipCardDetails: false 
        });
      }
    } else {
      // No subscription - normal flow
      setModalConfig({ 
        skipPlanSelection: false, 
        selectedPlan: null, 
        action: 'upgrade' 
      });
    }
    
    setIsModalOpen(true);
    localStorage.setItem("redirectAfterSubscribe", window.location.pathname);
  };

  // Function to refresh subscription data
  const refreshSubscriptionData = () => {
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleAddDealClick = (e) => {
    const limit = getDealLimit();
    if (deals.length >= limit) {
      e.preventDefault();
      setShowUpgradeBanner(true);
    }
  };

  const handleSaveTime = async () => {
    if (timeInput.trim()) {
      try {
        const response = await axiosInstance.put(
          `${process.env.REACT_APP_BASE_URL}business/profile/updateTime`,
          { lunchSpecialTime: timeInput },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const { responseMessage, responseCode } = response.data;
        fetchBusinessProfile(token);

        if (String(responseCode) !== "200" && String(responseCode) !== "201") {
          throw new Error(responseMessage?.[0] || "Failed to Add");
        }

        toast.success("Time saved successfully");
      } catch (error) {
        console.log(error.message);
        toast.error("Failed to save time");
      } finally {
        setEditingTime(false);
      }
    } else {
      toast.error("Please enter a valid time");
    }
  };

  const handleEditTime = () => {
    setTimeInput(savedTime || "");
    setEditingTime(true);
  };

  const handleCancelEdit = () => {
    setEditingTime(false);
  };

  const fetchBusinessProfile = async (token) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/business/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSavedTime(response.data?.responseData?.business.lunchSpecialTime || "");
      setLoading(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage?.[0] ||
        err.message ||
        "Failed to fetch business profile";
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchBusinessProfile(token);
      fetchSubscription(token);
    }
  }, [token, refreshTrigger]);

  const fetchDeals = async (page = 1, limit = pagination.limit, query = searchQuery) => {
    setLoading(true);
    setError(null);
    try {
      const client = ApiClient();
      const response = await client.get("business/deal/lunchSpecials/list", {
        params: {
          searchParam: query,
          limit: limit,
          page: page,
        },
      });
      const fetchedDeals = response.data.responseData.deals || [];
      const paginationData = response.data.responseData.pagination || {
        page: 1,
        totalPages: 1,
        totalDocs: 0,
        limit: 10,
      };
      setDeals(fetchedDeals);
      setPagination(paginationData);

      // Show banner if deal limit is reached
      if (fetchedDeals.length >= getDealLimit()) {
        setShowUpgradeBanner(true);
      } else {
        setShowUpgradeBanner(false);
      }

      if (fetchedDeals.length === 0 && query) {
        // toast.info("No matching deals found.");
      }
    } catch (error) {
      toast.error("Failed to fetch deals");
      setError("Failed to fetch deals. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchDeals = useCallback(
    debounce((page, limit, query) => {
      fetchDeals(page, limit, query);
    }, 500),
    [pagination.limit]
  );

  useEffect(() => {
    fetchDeals();
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedFetchDeals(1, pagination.limit, query);
  };

  const clearSearch = () => {
    setSearchQuery("");
    fetchDeals(1, pagination.limit, "");
  };

  const handlePageChange = (newPage) => {
    fetchDeals(newPage, pagination.limit, searchQuery);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination({ ...pagination, limit: newLimit });
    fetchDeals(1, newLimit, searchQuery);
  };

  const toggleActiveStatus = async (dealId, currentStatus) => {
    try {
      setLoading(true);
      const client = ApiClient();
      const response = await client.put(
        `business/deal/lunchSpecials/${dealId}/${!currentStatus}`
      );

      if (response.data.responseData && response.data.responseData.deal) {
        const updatedDeal = response.data.responseData.deal;
        setDeals((prevDeals) =>
          prevDeals.map((deal) =>
            deal._id === dealId
              ? { ...deal, isActive: updatedDeal.isActive }
              : deal
          )
        );

        toast.success(
          `Deal ${updatedDeal.isActive ? "activated" : "deactivated"} successfully`
        );
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error toggling deal status:", error);
      toast.error(
        error.response?.data?.responseMessage?.[0] ||
        "Failed to update deal status"
      );
    } finally {
      setLoading(false);
    }
  };

  const getDealLimit = () => {
    if (subscriptionLoading) return 1;
    switch (planName) {
      case "standard":
      case "sponsored":
        return 4;
      default:
        return 1;
    }
  };

  const confirmDelete = (deal) => {
    setSelectedDeal(deal);
    setDeleteModal(true);
  };

  const deleteDeal = async () => {
    try {
      const client = ApiClient();
      await client.delete(`business/deal/lunchSpecials/${selectedDeal._id}`);
      setDeals((prevDeals) => {
        const updatedDeals = prevDeals.filter(
          (deal) => deal._id !== selectedDeal._id
        );

        const limit = getDealLimit();
        if (updatedDeals.length < limit) {
          setShowUpgradeBanner(false);
        }

        return updatedDeals;
      });
      toast.success("Deal deleted successfully");
      setDeleteModal(false);
      setSelectedDeal(null);
    } catch (error) {
      toast.error("Failed to delete deal");
      setDeleteModal(false);
      setSelectedDeal(null);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSubscriptionSuccess = () => {
    setShowUpgradeBanner(false);
    setIsModalOpen(false);
    fetchDeals();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid className="mt-5">
          <MetaTags>
            <title>Lunch Specials List</title>
          </MetaTags>
          <ToastContainer />

          <Modal isOpen={deleteModal} centered>
            <ModalHeader>Confirm Delete</ModalHeader>
            <ModalBody>
              {selectedDeal && (
                <>
                  <p>
                    Do you really want to delete the deal{" "}
                    <strong>"{selectedDeal.name}"</strong>?
                  </p>
                  <p className="text-danger">This action cannot be undone.</p>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setDeleteModal(false)}>
                Cancel
              </Button>
              <Button color="danger" onClick={deleteDeal}>
                Yes, Delete
              </Button>
            </ModalFooter>
          </Modal>

          <PlanSelectionModal
            isOpen={isModalOpen && !isRefreshing}
            toggle={() => !isRefreshing && setIsModalOpen(!isModalOpen)}
            isUpgrade={!subscriptionDetails.type || subscriptionDetails.type === 'free' ? false : true}
            onSubscriptionSuccess={refreshSubscriptionData}
            modalConfig={modalConfig}
          />

          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader>
                  <Row>
                    <Col md={6}>
                      <h4 className="card-title mb-0">Lunch Specials</h4>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex justify-content-end align-items-center">
                        {savedTime && !editingTime ? (
                          <div className="d-flex align-items-center me-3">
                            <FontAwesomeIcon
                              icon={faClock}
                              className="me-2 text-muted"
                              style={{ fontSize: "16px" }}
                            />
                            <span className="me-2" style={{ fontSize: "16px" }}>
                              {savedTime}
                            </span>
                            <Button
                              color="link"
                              size="md"
                              onClick={handleEditTime}
                              className="p-0"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </Button>
                          </div>
                        ) : (
                          <InputGroup className="me-3" style={{ width: "400px" }}>
                            <InputGroupText>
                              <FontAwesomeIcon icon={faClock} />
                            </InputGroupText>
                            <Input
                              type="text"
                              placeholder="Enter time (e.g., 4PM-6PM)"
                              value={timeInput}
                              onChange={(e) => {
                                const value = e.target.value.toUpperCase();
                                if (/^[0-9:\-AMP\s]*$/.test(value)) {
                                  setTimeInput(value);
                                }
                              }}
                            />
                            <Button color="success" size="md" onClick={handleSaveTime}>
                              {savedTime ? "Update" : "Add"}
                            </Button>
                            {editingTime && (
                              <Button color="secondary" size="md" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                            )}
                          </InputGroup>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardHeader>

                {!subscriptionLoading && showUpgradeBanner && (
                  <div className="alert alert-warning d-flex justify-content-between align-items-center mt-3">
                    <span>
                      {planName === "free" ? (
                        <>You've reached the <b>free</b> plan limit (1 deal). Upgrade to add more deals.</>
                      ) : planName === "standard" ? (
                        <>You can only add up to 4 posts. To show top in your region, upgrade to <b>sponsored</b>.</>
                      ) : planName === "sponsored" ? (
                        <>You've reached your <b>sponsored</b> plan limit of 4 deals.</>
                      ) : null}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleBannerButtonClick}
                      disabled={isRefreshing}
                    >
                      {!subscriptionDetails.type || subscriptionDetails.type === 'free' ? "Add Plan" : "Upgrade Plan"}
                    </Button>
                  </div>
                )}

                <CardBody>
                  <div>
                    <Row className="g-4 mb-3">
                      <Col md={4}>
                        <div>
                          <h3>Deals List</h3>
                        </div>
                      </Col>
                      <Col md={8}>
                        <div className="d-flex justify-content-sm-end align-items-center position-relative mb-3">
                          <Link
                            to="/add-lunch-special"
                            className="btn btn-success me-2"
                            onClick={handleAddDealClick}
                          >
                            + Add Lunch Special Deal
                          </Link>
                          <div className="search-box ms-2 position-relative">
                            <Input
                              type="text"
                              className="form-control search"
                              placeholder="Search deals..."
                              value={searchQuery}
                              onChange={handleSearchInputChange}
                              onKeyPress={(e) =>
                                e.key === "Enter" &&
                                fetchDeals(1, pagination.limit, searchQuery)
                              }
                            />
                            <i className="ri-search-line search-icon"></i>
                            {searchQuery && (
                              <i
                                className="ri-close-line clear-icon"
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  color: "#666",
                                }}
                                onClick={clearSearch}
                              />
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  {error && <p className="text-danger">{error}</p>}
                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "300px" }}
                    >
                      <div className="text-center">
                        <Spinner
                          color="primary"
                          style={{ width: "3rem", height: "3rem" }}
                        />
                        <p className="mt-2">Loading deals...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="live-preview">
                      <div className="table-responsive table-card">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">Deal Photo</th>
                              <th scope="col">Name</th>
                              <th scope="col">Price</th>
                              <th scope="col">Category</th>
                              <th scope="col">Status</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deals.length > 0 ? (
                              deals.map((deal) => (
                                <tr key={deal._id}>
                                  <td>
                                    {deal.dealPhoto?.url ? (
                                      <img
                                        src={deal.dealPhoto.url}
                                        alt={deal.name}
                                        style={{
                                          width: "60px",
                                          height: "60px",
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                          objectFit: "contain",
                                        }}
                                      />
                                    ) : (
                                      <p className="mb-0">No Image</p>
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      maxWidth: "200px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {deal.name}
                                  </td>
                                  <td>$ {deal.price}</td>
                                  <td>{deal.category?.name || "N/A"}</td>
                                  <td>
                                    <Badge color={deal.isActive ? "success" : "danger"}>
                                      {deal.isActive ? "Available" : "Unavailable"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className={`btn btn-sm ms-2 ${
                                          deal.isActive ? "btn-danger" : "btn-success"
                                        }`}
                                        onClick={() => toggleActiveStatus(deal._id, deal.isActive)}
                                        disabled={loading}
                                      >
                                        {deal.isActive ? "Deactivate Deal" : "Activate Deal"}
                                      </button>
                                      <Link to={`/lunch-deal/${deal._id}`}>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                        >
                                          Show Detail
                                        </button>
                                      </Link>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={() => confirmDelete(deal)}
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                      <Link to={`/edit-lunch-deal/${deal._id}`}>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                        >
                                          <FontAwesomeIcon icon={faPencil} />
                                        </button>
                                      </Link>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">Sorry! No Result Found</h5>
                                    <p className="text-muted mb-0">
                                      We've searched for deals but did not find any matching results.
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                      <div className="d-flex justify-content-end mt-3">
                        <div className="pagination-wrap hstack gap-2">
                          <Link
                            className={`page-item pagination-prev ${
                              pagination.page === 1 ? "disabled" : ""
                            }`}
                            to="#"
                            onClick={() => handlePageChange(pagination.page - 1)}
                          >
                            Previous
                          </Link>
                          <ul className="pagination listjs-pagination mb-0">
                            {Array.from(
                              { length: pagination.totalPages },
                              (_, i) => (
                                <li
                                  key={i}
                                  className={`page-item ${
                                    pagination.page === i + 1 ? "active" : ""
                                  }`}
                                >
                                  <Link
                                    className="page-link paginationlink"
                                    to="#"
                                    onClick={() => handlePageChange(i + 1)}
                                  >
                                    {i + 1}
                                  </Link>
                                </li>
                              )
                            )}
                          </ul>
                          <Link
                            className={`page-item pagination-next ${
                              pagination.page === pagination.totalPages ? "disabled" : ""
                            }`}
                            to="#"
                            onClick={() => handlePageChange(pagination.page + 1)}
                          >
                            Next
                          </Link>
                        </div>
                        <select
                          className="form-select ms-2"
                          style={{ width: "100px" }}
                          value={pagination.limit}
                          onChange={handleLimitChange}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </select>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default LunchDealList;